import type { FetchError } from 'ofetch'
import type { RouteLocationNormalizedGeneric } from 'vue-router'
import type { Image, SourceType } from '~/types'

export const generateList = (first = 0, last = 0) => {
  const days = []

  for (let i = first; i <= last; i++) {
    days.push(`${i < 10 ? `0${i}` : i}`)
  }

  return days
}

export const formatNumber = (num?: string | number) => {
  if (num == undefined) return num

  const formattedNumber = num
    .toString()
    .replace('.', ',')
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')

  return `${formattedNumber} pts`
}

export const formatRun = (num?: number, isSecondsFormat = false) => {
  if (num == undefined) return num

  if (isSecondsFormat) {
    const remainingSeconds = Math.floor(num / 1000)
    const minutes = Math.floor((remainingSeconds / 60) % 60)
    const secondes = Math.floor(remainingSeconds % 60)

    return `${minutes}m ${secondes.toString().padStart(2, '0')}s`
  }

  const min = Math.floor(num / (60 * 1000))

  const rest = num % (60 * 1000)

  const sec = Math.floor(rest / 1000)
  const ms = rest % 1000

  return `${min}m ${sec.toString().padStart(2, '0')}s ${ms
    .toString()
    .padStart(3, '0')}ms`
}

export const findYoutubeId = (str: string) => {
  const matches = str.match(
    /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/(?:embed\/)?)([^\s&]+)/,
  )

  return matches ? matches[1] : null
}

export const findDailymotionId = (str: string) => {
  const matches = str.match(
    /(?:https?:\/{2})?(?:w{3}\.)?dai(?:lymotion)?\.(?:com|ly)\/(?:embed\/)?(?:video\/)?([^\s&]+)/,
  )

  return matches ? matches[1] : null
}

export const findVimeoId = (str: string) => {
  const matches = str.match(
    /(?:https?:\/{2})?(?:w{3}\.)?(?:player.)?vimeo\.com(?:\/video|\/)([^\s&]+)/,
  )

  return matches ? matches[1] : null
}

export const getVideoInfo = (videoUrl?: string) => {
  const defaultValue = {
    isYoutube: false,
    cover: '',
  }

  if (!videoUrl) return defaultValue

  const videoData = extractVideoId(videoUrl)

  if (!videoData) return defaultValue

  const isYoutube = videoData.source === 'youtube'

  return {
    isYoutube,
    cover: isYoutube
      ? videoData.id
      : buildEmbedUrl(videoData.source, videoData.id),
  }
}

export const extractVideoId = ((url?: string) => {
  if (!url) return
  const youtubeId = findYoutubeId(url)

  if (youtubeId) {
    return {
      source: 'youtube',
      id: youtubeId,
    } as const
  }

  const dailymotionId = findDailymotionId(url)

  if (dailymotionId) {
    return {
      source: 'dailymotion',
      id: dailymotionId,
    } as const
  }

  const vimeoId = findVimeoId(url)

  if (vimeoId) {
    return {
      source: 'vimeo',
      id: vimeoId,
    } as const
  }
}) satisfies (str: string) => { source: SourceType; id: string } | undefined

export const buildYoutubeEmbedUrl = (id: string | number) =>
  `https://www.youtube.com/embed/${id}`
export const buildDailymotionEmbedUrl = (id: string | number) =>
  `https://www.dailymotion.com/embed/video/${id}`
export const buildVimeoEmbedUrl = (id: string | number) =>
  `https://player.vimeo.com/video/${id}`

export const buildEmbedUrl = (
  source: SourceType | undefined,
  id: string | number | undefined,
) => {
  if (!source || !id) return undefined

  if (source === 'youtube') {
    return buildYoutubeEmbedUrl(id)
  }

  if (source === 'dailymotion') {
    return buildDailymotionEmbedUrl(id)
  }

  if (source === 'vimeo') {
    return buildVimeoEmbedUrl(id)
  }

  return undefined
}

const EMBED_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'www.dailymotion.com',
  'player.vimeo.com',
]

/**
 * The embed URL of a post or comment is user input that ends up in <iframe src>:
 * keep it only when it is an https URL of a known video host (a "javascript:" src runs in the page).
 * Same rule as BaseController::isSafeEmbedUrl on the API.
 */
export const safeEmbedUrl = (url?: string | null) => {
  if (!url) return undefined
  try {
    const parsed = new URL(url.trim())
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      parsed.port ||
      !EMBED_HOSTS.includes(parsed.hostname)
    ) {
      return undefined
    }
    return parsed.href
  } catch {
    return undefined
  }
}

/** For values interpolated in an HTML attribute of a string passed to v-html */
export const escapeHtmlAttribute = (value?: string) =>
  (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

export const linkify = (inputText?: string) => {
  let replacedText = inputText || ''

  // URLs starting with http:// or https://
  const replacePattern1 =
    // eslint-disable-next-line no-useless-escape
    /([^>"'])(\bhttps?:\/\/[\/\w\-+&@#%?=~_|!:,.;]*)(?![^[\/\w\-+&@#%?=~_|!:,.;]])/gim
  // URLs starting with "www." (without // before it, or it'd re-link the ones done above).
  // const replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim
  // Change email addresses to mailto:: links.
  // const replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim

  replacedText = replacedText.replace(
    replacePattern1,
    '$1<a href="$2" target="_blank" rel="noopener noreferrer nofollow">$2</a> ',
  )

  // replacedText = replacedText.replace(
  //   replacePattern2,
  //   '$1<a href="https://$2" target="_blank" rel="noopener noreferrer nofollow">$2</a>',
  // )

  // replacedText = replacedText.replace(
  //   replacePattern3,
  //   '<a href="mailto:$1" target="_blank" rel="noopener noreferrer nofollow">$1</a>',
  // )

  return replacedText
}

export const isNumber = (value?: number): value is number =>
  typeof value === 'number'

//const UNIQUE_KEY_PROP = "__unique_key_prop__";
//const KEY_PREFIX = "__key_prefix__" + Date.now() + "_";
//let uid = 0;

export const isDefined = <T>(value: T | undefined): value is T =>
  value !== undefined

/**
 * The header images of an item: the browser picks the mobile one (<source media> in HeaderImage), so the choice must
 * not depend on the screen here (the server has no screen: it answered the desktop image to every mobile)
 */
export const getImages = (
  item: MaybeRef<
    | {
        image_desktop?: Image
        image_mobile?: Image
        image_portrait?: Image
      }
    | undefined
  >,
) => {
  const rawItem = unref(item)
  const image = rawItem?.image_desktop?.url

  return {
    image,
    imageMobile: rawItem?.image_mobile?.url ?? rawItem?.image_portrait?.url,
  }
}

type MaybeRefOrComputed<T> = T | Ref<T> | ComputedRef<T>

export const uniqueBy = <T extends Record<string, unknown>>(
  array: MaybeRefOrComputed<T[]>,
  property: string,
) => {
  return computed(() => {
    const rawArray = unref(array)
    if (!rawArray.length) return rawArray

    const seenSlugs = new Set()
    return rawArray.filter((item) => {
      if (seenSlugs.has(item[property])) {
        return false
      }

      seenSlugs.add(item[property])
      return true
    })
  })
}

// Accepts relative paths too (e.g. the '/img/default-thumb.jpg' fallback of a card without image): new URL() without
// a base throws "Invalid URL" on them, which failed the SSR of the whole page.
export const getURLPathname = (url: string) =>
  new URL(url, 'http://localhost').pathname

export const createError404 = (error: FetchError) => {
  if (error.statusCode === 404) {
    throw createError({ statusCode: 404 })
  }
}

/**
 * Store actions log fetch errors and leave their entity empty instead of throwing
 * (they are shared with modals), so a page loader checks the entity once loaded:
 * an unknown slug must answer a real 404, not a 200 with an empty page.
 */
export const throwIfNotFound = (entity: unknown) => {
  if (!entity) {
    throw createError({ statusCode: 404 })
  }
}

export const getPageQuery = (route: RouteLocationNormalizedGeneric) => {
  return route.query.page
    ? Number.parseInt(route.query.page as unknown as string, 10)
    : 1
}

export const isAstrobotContestRunning = () => {
  const currentDate = new Date().getTime()
  const startDate = new Date('2024-09-06').getTime()
  const endDate = new Date('2024-09-26T10:00:00').getTime()

  return currentDate >= startDate && currentDate <= endDate
}
